import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { merge, catchError, map, tap, Observable, Subject, of } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DestroyRxComponent } from '../../components/destroy-rx/destroy-rx.component';
import { RouterService } from './router.service';
import { System } from '../types/system';
import { Dynamic } from '../types/dynamic';
import {
    MetadataObject,
    MetadataTarget,
    MetadataObjectOptions,
    MetadataObjectBase,
    MetadataObjectValue,
    MetadataAnalysisPosition,
    MetadataItem,
} from '../types/metadata/MetadataObject';
import { BillSummary } from '../types/bills/BillSummary';

@Injectable({
    providedIn: 'root',
})
export class MetadataService extends DestroyRxComponent {
    private url = this.routerService.getApiUrl('metadata');
    private billUrl = `${this.url}/bill`;
    private objUrl = `${this.url}/object`;
    private objsUrl = `${this.url}/objects`;
    private savedStatus$ = new Subject<System>();
    private initStatus$ = this.http
        .get<Dynamic>(`${this.url}/${Endpoints.SystemStatus}`)
        .pipe(
            tap((data) =>
                console.log(`System status: ${JSON.stringify(data)}`),
            ),
            map(this.mapToStatus),
            catchError(this.handleError),
        );

    status$ = merge(this.initStatus$, this.savedStatus$);

    constructor(
        private http: HttpClient,
        private routerService: RouterService,
    ) {
        super();
    }

    /**
     * Gets bill items by bill id.
     * @param id The bill id.
     */
    public getBillItems(id: string): Observable<MetadataObject[]> {
        const url = `${this.billUrl}/${id}/items`;

        return this.http.get<MetadataObject[]>(url);
    }

    public getSummaryTypes(): Observable<MetadataObject> {
        const url = `${this.url}/summarytypes`;

        return this.http.get<MetadataObject>(url);
    }

    /**
     * Gets bill summaries by bill id.
     * @param id The bill id.
     */
    public getBillSummaries(id: string): Observable<BillSummary[]> {
        const url = `${this.billUrl}/${id}/summaries`;

        return this.http.get<BillSummary[]>(url);
    }

    /**
     * Saves a bill summary by bill id.
     * @param id The bill id.
     * @param summary The bill summary to save.
     */
    public saveBillSummary(id: string, summary: BillSummary) {
        const url = `${this.billUrl}/${id}/summaries`;

        return this.http.post(url, summary);
    }

    /**
     * Saves the system status displayed on the dashboard.
     * @param status The new status.
     */
    public saveSystemStatus(status: string): void {
        const body = { statusHtml: status };
        this.http
            .put<Dynamic>(`${this.url}/${Endpoints.SystemStatus}`, body)
            .pipe(
                tap((data) =>
                    console.log(`Saved system status: ${JSON.stringify(data)}`),
                ),
                map(this.mapToStatus),
                takeUntil(this.destroy$),
                catchError(this.handleError),
            )
            .subscribe((s) => this.savedStatus$.next(s));
    }

    /**
     * Gets all metadata targets.
     */
    public getTargets() {
        const url = `${this.url}/targets`;
        return this.http.get<MetadataTarget[]>(url);
    }

    /**
     * Gets a single metadata object by id.
     * @param id The id of the metadata object.
     */
    public getObject(id: number) {
        const url = `${this.objUrl}/${id}`;
        return this.http.get<MetadataObject>(url);
    }

    /**
     * Gets all metadata objects by type.
     * @param type The type of metadata objects. (default type = 'all')
     */
    public getObjects(type = 'all') {
        const url = `${this.objsUrl}/${type}`;
        return this.http.get<MetadataObject[]>(url);
    }

    /**
     * Updates a metadata object's status.
     * @param object The updated metadata object.
     */
    public updateObjectStatus(object: MetadataObject) {
        const url = `${this.objUrl}/update`;
        return this.http.post<MetadataObject>(url, object);
    }

    /**
     * Updates all metadata object attributes and values.
     * @param object The updated metadata object.
     */
    public updateObject(object: MetadataObject) {
        const url = `${this.objUrl}/updateobject`;
        return this.http.post<MetadataObject>(url, object);
    }

    /**
     * Updates a metadata object's values including the ordering.
     * @param id The id of the metadata object.
     * @param values A tuple containing the datatype name and the object's values.
     */
    public updateObjectValues(
        id: number,
        values: [string, MetadataObjectValue[]],
    ) {
        const url = `${this.objUrl}/${id}/values`;
        return this.http.post<MetadataObjectValue[]>(url, values);
    }

    /**
     * Add a new value to a metadata object's values.
     * @param id The id of the metadata object.
     * @param value A tuple containing the datatype name and the new value.
     */
    public addObjectValue(id: number, value: [string, MetadataObjectValue]) {
        const url = `${this.objUrl}/${id}/value`;
        return this.http.post<MetadataObjectValue>(url, value);
    }

    /**
     * Saves a newly created metadata object.
     * @param object The new metadata object.
     */
    public saveObject(object: MetadataObjectBase) {
        return this.http.post<MetadataObject>(this.objUrl, object);
    }

    /**
     * Gets all metadata object options.
     */
    public getObjectOptions() {
        const url = `${this.objsUrl}/options`;
        return this.http.get<MetadataObjectOptions>(url);
    }

    /**
     * Gets all Analysis Position options.
     */
    public getAnalysisPositions(): Observable<MetadataAnalysisPosition[]> {
        const url = `${this.url}/analysis-positions/`;

        return this.http.get<MetadataAnalysisPosition[]>(url);
    }

    /**
     * Gets Analysis Position for an Analysis.
     * @param number Analysis Id to get selected position for
     */
    public getAnalysisPosition(analysisId: number): Observable<number> {
        const url = `${this.url}/analysis/${analysisId}/position/`;

        return this.http.get<number>(url);
    }

    /**
     * Sets Analysis Position for an Analysis.
     * @param number Analysis Id to get selected position for
     * @param number Key of position to set for Analysis
     */
    public setAnalysisPosition(analysisId: number, value: number) {
        const url = `${this.url}/analysis/${analysisId}/position/${value}`;

        return this.http.post(url, null);
    }

    /**
     * Save Item to Target
     * @param MetadataItem item to save
     */
    public saveItem(item: MetadataItem) {
        const url = `${this.url}/items/save`;

        return this.http.post(url, item);
    }

    /**
     * Remove Item from Target
     * @param MetadataItem item to remove
     */
    public removeItem(item: MetadataItem) {
        const url = `${this.url}/item/remove`;

        return this.http.post(url, item);
    }

    private handleError(error: HttpErrorResponse): Observable<System> {
        console.error(error);
        return of({ status: '<h1>System status unavailable</h1>' });
    }

    private mapToStatus(data: Dynamic): System {
        return { status: data[Object.keys(data)[0]] };
    }
}

enum Endpoints {
    SystemStatus = 'system-status',
}
